import { ExerciseDetail } from "../../../components/AthleteDashboard";
import {
	hasContent,
	getForTimeRoundsPrefix,
	getEmomRoundLabel,
	formatEmomItemSummary,
	resolveConditioningResultPresentation,
} from "../../../lib/conditioningResultUtils";
import {
	formatStrengthRepResult,
	getStrengthPrescriptionRepsForSet,
	isDistanceStrengthReps,
	isTimedStrengthReps,
	RUNNING_TYPES,
} from "../../../lib/programBuilderUtils";
import { formatEmomHeadline } from "../../../lib/emomSummary";
import { ShootingResultBlock } from "../../../components/shooting/ShootingResultBlock";

const isMeaningful = (value) => {
	if (!hasContent(value)) return false;
	const s = String(value).trim();
	return s !== "0" && s !== "0.0";
};

const formatSeconds = (totalSeconds) => {
	if (!isMeaningful(totalSeconds)) return null;
	const sec = Number(totalSeconds);
	if (!Number.isFinite(sec) || sec < 0) return null;
	const m = Math.floor(sec / 60);
	const s = Math.round(sec % 60);
	return `${m}:${String(s).padStart(2, "0")}`;
};

function formatSchemaValue(rawValue, unit) {
	if (rawValue === null || rawValue === undefined || rawValue === "")
		return null;
	const u = String(unit || "").toLowerCase();
	if (u === "minutes_seconds" || u === "time") {
		const total = Number(rawValue);
		if (Number.isFinite(total) && total >= 0) {
			const m = Math.floor(total / 60);
			const s = Math.round(total % 60);
			return `${m}:${String(s).padStart(2, "0")}`;
		}
	}
	if (u === "kg") return `${rawValue} kg`;
	if (u === "meters") return `${rawValue} m`;
	if (u === "reps") return `${rawValue} reps`;
	return String(rawValue);
}

function ConditioningResultBlock({
	exercise,
	result,
	language,
	isCoachView = false,
}) {
	const view = resolveConditioningResultPresentation({
		result,
		prescriptionExercise: exercise,
		programExercise: exercise,
		language,
	});
	const {
		condType,
		emomRounds,
		rounds,
		roundsLabel,
		timeCap,
		interval,
		displayScore,
		amrapScoreLabel,
		displayNotes,
	} = view;

	const forTimePrefix =
		condType === "for_time"
			? getForTimeRoundsPrefix(rounds, roundsLabel, language)
			: "";

	// Score "principale" da mostrare nella riga score: privilegia AMRAP formatted
	// se applicabile, altrimenti displayScore generico.
	const primaryScore =
		condType === "amrap" && amrapScoreLabel ? amrapScoreLabel : displayScore;

	// Schema-based result: itera result_schema dell'esercizio e mostra i valori
	// loggati da result.schema_values.
	const schemaFields = Array.isArray(exercise?.result_schema)
		? exercise.result_schema
		: [];
	const schemaValues =
		result?.schema_values && typeof result.schema_values === "object"
			? result.schema_values
			: null;
	const schemaRows = schemaValues
		? schemaFields
				.map((field) => {
					const fid = field?.field_id || field?.name;
					if (!fid) return null;
					const raw = schemaValues[fid];
					const formatted = formatSchemaValue(raw, field?.unit);
					if (!formatted) return null;
					// Label visibile: ConditioningResultSchemaEditor usa `title` come
					// etichetta umana del campo. Fallback su altri campi storici.
					const label =
						field?.title ||
						field?.label ||
						field?.name ||
						field?.display_label ||
						"Valore";
					return { label, value: formatted };
				})
				.filter(Boolean)
		: [];

	// Flag per capire se almeno UN dato è renderizzabile (oltre al label tipo).
	const hasAnyData =
		hasContent(primaryScore) ||
		isMeaningful(result?.time_seconds) ||
		isMeaningful(result?.weight_kg) ||
		hasContent(result?.reps_text) ||
		(result?.interval_scores || []).length > 0 ||
		isMeaningful(result?.reps) ||
		schemaRows.length > 0 ||
		(condType === "emom" && emomRounds.length > 0) ||
		isMeaningful(result?.rpe) ||
		hasContent(result?.scaling) ||
		hasContent(displayNotes) ||
		hasContent(result?.notes);

	return (
		<div className="space-y-1.5 text-xs">
			{/* Header tipo conditioning */}
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
				{condType === "for_time" && (
					<span className="font-medium text-foreground">
						{forTimePrefix ? `${forTimePrefix} ` : ""}For Time
						{timeCap && (
							<span className="text-muted-foreground font-normal">
								{" "}
								· TC {Math.floor(timeCap / 60)}'
							</span>
						)}
					</span>
				)}
				{condType === "amrap" && (
					<span className="font-medium text-foreground">
						{timeCap ? `${Math.floor(timeCap / 60)}' ` : ""}AMRAP
					</span>
				)}
				{condType === "emom" && (
					<span className="font-medium text-foreground">
						{formatEmomHeadline(
							{ rounds, interval_seconds: interval },
							language,
						)}
					</span>
				)}
				{condType === "hiit" && (
					<span className="font-medium text-foreground">HIIT</span>
				)}
				{(condType === "free" || condType === "custom" || !condType) && (
					<span className="font-medium text-foreground">Conditioning</span>
				)}
			</div>

			{/* Coach view: mostra SOLO i dati dettagliati, NON il score recap (già in card) */}
			{isCoachView ? (
				<>
					{/* AMRAP: mostra il punteggio con i round calcolati (non presente nel
					    recap card, che riporta solo il valore grezzo) */}
					{amrapScoreLabel && (
						<div className="text-volt">
							<span className="text-muted-foreground">Risultato: </span>
							{amrapScoreLabel}
						</div>
					)}
					{/* Dettagli senza recap score */}
					{isMeaningful(result?.time_seconds) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Tempo: </span>
							{formatSeconds(result.time_seconds)}
						</div>
					)}
					{isMeaningful(result?.weight_kg) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Carico: </span>
							{result.weight_kg} kg
						</div>
					)}
					{(hasContent(result?.reps_text) || isMeaningful(result?.reps)) && (
						<div>
							<span className="text-muted-foreground">Reps: </span>
							{result.reps_text || result.reps}
						</div>
					)}
					{(result?.interval_scores || []).length > 0 && (
						<div>
							<span className="text-muted-foreground">Ripetute: </span>
							{result.interval_scores.join(" · ")}
						</div>
					)}
					{schemaRows.length > 0 && (
						<div className="space-y-0.5">
							{schemaRows.map((row, j) => (
								<div key={j}>
									<span className="text-muted-foreground">{row.label}: </span>
									<span className="text-foreground">{row.value}</span>
								</div>
							))}
						</div>
					)}
					{condType === "emom" && emomRounds.length > 0 && (
						<div className="space-y-1 pt-1">
							{emomRounds.map((round, j) => (
								<div
									key={round.round_id || j}
									className="border border-amber-400/20 bg-amber-400/5 rounded-sm px-2 py-1.5"
								>
									<div className="text-[10px] uppercase tracking-wider text-amber-400 font-medium mb-0.5">
										{getEmomRoundLabel(round, j, language)}
									</div>
									{(round.items || []).map((item, k) => (
										<span
											key={item.item_id || `${j}-${k}`}
											className="block text-[11px] text-muted-foreground"
										>
											{formatEmomItemSummary(item, language)}
										</span>
									))}
								</div>
							))}
						</div>
					)}
					{isMeaningful(result?.rpe) && (
						<div>
							<span className="text-muted-foreground">RPE:</span> {result.rpe}
						</div>
					)}
					{hasContent(result?.scaling) && (
						<div>
							<span className="text-muted-foreground">Scaling:</span>{" "}
							{result.scaling}
						</div>
					)}
					{hasContent(displayNotes) && (
						<div className="pt-1 italic text-muted-foreground">
							"{displayNotes}"
						</div>
					)}
					{!hasAnyData && (
						<div className="text-muted-foreground/60 italic">
							Nessun dettaglio registrato.
						</div>
					)}
				</>
			) : (
				<>
					{/* Original athlete view: mostra score recap + dettagli */}
					{/* Dati registrati — mostro qualunque campo abbia valore, indipendentemente dal condType */}
					{hasContent(primaryScore) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Score: </span>
							{primaryScore}
						</div>
					)}
					{isMeaningful(result?.time_seconds) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Tempo: </span>
							{formatSeconds(result.time_seconds)}
						</div>
					)}
					{isMeaningful(result?.weight_kg) && (
						<div className="text-volt">
							<span className="text-muted-foreground">Carico: </span>
							{result.weight_kg} kg
						</div>
					)}
					{(hasContent(result?.reps_text) || isMeaningful(result?.reps)) && (
						<div>
							<span className="text-muted-foreground">Reps: </span>
							{result.reps_text || result.reps}
						</div>
					)}
					{(result?.interval_scores || []).length > 0 && (
						<div>
							<span className="text-muted-foreground">Ripetute: </span>
							{result.interval_scores.join(" · ")}
						</div>
					)}

					{/* Schema-based result (es. conditioning custom con campi multipli) */}
					{schemaRows.length > 0 && (
						<div className="space-y-0.5">
							{schemaRows.map((row, j) => (
								<div key={j}>
									<span className="text-muted-foreground">{row.label}: </span>
									<span className="text-foreground">{row.value}</span>
								</div>
							))}
						</div>
					)}

					{/* Round EMOM con item logged (dalla prescrizione) */}
					{condType === "emom" && emomRounds.length > 0 && (
						<div className="space-y-1 pt-1">
							{emomRounds.map((round, j) => (
								<div
									key={round.round_id || j}
									className="border border-amber-400/20 bg-amber-400/5 rounded-sm px-2 py-1.5"
								>
									<div className="text-[10px] uppercase tracking-wider text-amber-400 font-medium mb-0.5">
										{getEmomRoundLabel(round, j, language)}
									</div>
									{(round.items || []).map((item, k) => (
										<span
											key={item.item_id || `${j}-${k}`}
											className="block text-[11px] text-muted-foreground"
										>
											{formatEmomItemSummary(item, language)}
										</span>
									))}
								</div>
							))}
						</div>
					)}

					{/* RPE / scaling / notes */}
					{isMeaningful(result?.rpe) && (
						<div>
							<span className="text-muted-foreground">RPE:</span> {result.rpe}
						</div>
					)}
					{hasContent(result?.scaling) && (
						<div>
							<span className="text-muted-foreground">Scaling:</span>{" "}
							{result.scaling}
						</div>
					)}
					{hasContent(displayNotes) && (
						<div className="pt-1 italic text-muted-foreground">
							"{displayNotes}"
						</div>
					)}

					{/* Stato vuoto esplicito: il result esiste ma è privo di dati visualizzabili.
					    Indica un possibile bug di salvataggio o un risultato troncato. */}
					{!hasAnyData && (
						<div className="text-muted-foreground/60 italic">
							Risultato registrato senza dettagli numerici. Apri il dettaglio
							per verificare.
						</div>
					)}
				</>
			)}
		</div>
	);
}

function StrengthResultBlock({ result, prescription, isCoachView = false }) {
	const setRows = Array.isArray(result.result_set_rows)
		? result.result_set_rows
		: [];
	const supersetDetails = Array.isArray(result.superset_details)
		? result.superset_details
		: [];
	const hasDetailed = setRows.length > 0 || supersetDetails.length > 0;
	const getDisplaySetNumber = (row, idx) => {
		const rawIndex = Number(row?.row_index);
		if (Number.isFinite(rawIndex) && rawIndex >= 0) {
			return rawIndex + 1;
		}
		return idx + 1;
	};

	return (
		<div className="space-y-1.5 text-xs">
			{/* Superset: una riga per sub-esercizio */}
			{supersetDetails.length > 0 && (
				<div className="space-y-1">
					<div className="text-[10px] uppercase tracking-wider text-muted-foreground">
						Superset
					</div>
					{supersetDetails.map((d, j) => (
						<div
							key={j}
							className="flex flex-wrap items-baseline gap-x-2 bg-secondary/40 rounded-sm px-2 py-1.5"
						>
							<span className="font-medium text-foreground">
								<span className="text-volt mr-1">
									{String.fromCharCode(65 + j)}:
								</span>
								{d.exercise_name || "—"}
							</span>
							{isMeaningful(d.weight_kg) && (
								<span className="text-volt">{d.weight_kg} kg</span>
							)}
							{isMeaningful(d.reps) && (
								<span className="text-muted-foreground">· {d.reps} reps</span>
							)}
							{isMeaningful(d.rpe) && (
								<span className="text-acid">· RPE {d.rpe}</span>
							)}
						</div>
					))}
				</div>
			)}

			{/* Set-by-set strength */}
			{setRows.length > 0 && (
				<div className="space-y-1">
					<div className="text-[10px] uppercase tracking-wider text-muted-foreground">
						Set eseguiti
					</div>
					{setRows.map((row, j) => {
						const reps = hasContent(row.reps_text) ? row.reps_text : row.reps;
						const rowIndex = Number.isInteger(Number(row?.row_index))
							? Number(row.row_index)
							: j;
						const prescribedReps = getStrengthPrescriptionRepsForSet(
							prescription,
							rowIndex,
						);
						if (
							!isMeaningful(row.weight_kg) &&
							!isMeaningful(reps) &&
							!isMeaningful(row.rpe)
						)
							return null;
						return (
							<div
								key={j}
								className="flex flex-wrap items-baseline gap-x-2 bg-secondary/40 rounded-sm px-2 py-1.5"
							>
								<span className="text-muted-foreground w-12">
									Set {getDisplaySetNumber(row, j)}
								</span>
								{isMeaningful(row.weight_kg) && (
									<span className="text-volt font-medium">
										{row.weight_kg} kg
									</span>
								)}
								{isMeaningful(reps) && (
									<span className="text-muted-foreground">
										· {formatStrengthRepResult(reps, prescribedReps)}
									</span>
								)}
								{isMeaningful(row.rpe) && (
									<span className="text-acid">· RPE {row.rpe}</span>
								)}
							</div>
						);
					})}
				</div>
			)}

			{/* Riepilogo sintetico (se NO detailed) */}
			{!hasDetailed && (
				<div className="space-y-0.5">
					{isMeaningful(result.weight_kg) && (
						<div>
							<span className="text-muted-foreground">Carico:</span>{" "}
							<span className="text-volt font-medium">
								{result.weight_kg} kg
							</span>
						</div>
					)}
					{(hasContent(result.reps_text) || isMeaningful(result.reps)) && (
						<div>
							<span className="text-muted-foreground">
								{isTimedStrengthReps(
									prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
								)
									? "Tempo:"
									: isDistanceStrengthReps(
												prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
										  )
										? "Distanza:"
										: "Reps:"}
							</span>{" "}
							{isTimedStrengthReps(
								prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
							) ||
							isDistanceStrengthReps(
								prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
							)
								? formatStrengthRepResult(
										result.reps_text || result.reps,
										prescription?.set_rows?.[0]?.reps ?? prescription?.reps,
									)
								: result.reps_text || result.reps}
						</div>
					)}
					{isMeaningful(result.time_seconds) && (
						<div>
							<span className="text-muted-foreground">Tempo:</span>{" "}
							{formatSeconds(result.time_seconds)}
						</div>
					)}
					{hasContent(result.score) && (
						<div>
							<span className="text-muted-foreground">Score:</span>{" "}
							{result.score}
						</div>
					)}
				</div>
			)}

			{/* RPE / scaling / notes (comuni anche con detailed) */}
			{!hasDetailed && isMeaningful(result.rpe) && (
				<div>
					<span className="text-muted-foreground">RPE:</span> {result.rpe}
				</div>
			)}
			{hasContent(result.scaling) && (
				<div>
					<span className="text-muted-foreground">Scaling:</span>{" "}
					{result.scaling}
				</div>
			)}
			{hasContent(result.notes) && (
				<div className="pt-1 italic text-muted-foreground">
					"{result.notes}"
				</div>
			)}
		</div>
	);
}

function RunningResultBlock({ exercise, result }) {
	const runningType = String(exercise?.running_type || "")
		.trim()
		.toLowerCase();
	const typeLabel = RUNNING_TYPES.find(
		(item) => item.value === runningType,
	)?.label;
	const titleLabel =
		runningType && runningType !== "custom" && typeLabel
			? `Running - ${typeLabel}`
			: "Running";

	const intervalScores = Array.isArray(result?.interval_scores)
		? result.interval_scores.filter((value) => hasContent(value))
		: [];
	const intervalText =
		intervalScores.join(" · ") ||
		(hasContent(result?.reps_text) ? result.reps_text : "");
	// I parziali sono ripetute (interval_rows) o tratti (fartlek_segments):
	// la prescrizione dice quali, e l'etichetta segue.
	const hasIntervalRows =
		Array.isArray(exercise?.interval_rows) && exercise.interval_rows.length > 0;
	const intervalLabel = hasIntervalRows ? "Ripetute" : "Tratti";
	const timeText = formatSeconds(result?.time_seconds);
	const distanceText = hasContent(result?.score) ? result.score : "";
	// Passo medio dell'uscita, CALCOLATO da tempo e distanza registrati: non
	// e' un dato dell'orologio e la vista lo dice. Senza uno dei due, niente.
	const averagePace = (() => {
		const seconds = Number(result?.time_seconds);
		const km = Number(result?.score);
		if (!Number.isFinite(seconds) || !Number.isFinite(km) || seconds <= 0 || km <= 0)
			return "";
		const perKm = seconds / km;
		const m = Math.floor(perKm / 60);
		const s = Math.round(perKm % 60);
		return `${m}:${String(s === 60 ? 0 : s).padStart(2, "0")}/km`;
	})();
	const avgHr = isMeaningful(result?.avg_heart_rate) ? result.avg_heart_rate : "";
	const maxHr = isMeaningful(result?.max_heart_rate) ? result.max_heart_rate : "";

	return (
		<div className="space-y-1.5 text-xs">
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
				<span className="font-medium text-foreground">{titleLabel}</span>
			</div>
			{timeText && (
				<div>
					<span className="text-muted-foreground">Tempo: </span>
					{timeText}
				</div>
			)}
			{distanceText && (
				<div>
					<span className="text-muted-foreground">Distanza: </span>
					{distanceText} km
				</div>
			)}
			{averagePace && (
				<div>
					<span className="text-muted-foreground">Passo medio: </span>
					{averagePace}
					<span className="text-muted-foreground"> (calcolato)</span>
				</div>
			)}
			{(avgHr || maxHr) && (
				<div>
					<span className="text-muted-foreground">FC: </span>
					{avgHr ? `media ${avgHr}` : ""}
					{avgHr && maxHr ? " · " : ""}
					{maxHr ? `max ${maxHr}` : ""}
				</div>
			)}
			{intervalText && (
				<div>
					<span className="text-muted-foreground">{intervalLabel}: </span>
					{intervalText}
				</div>
			)}
			{isMeaningful(result?.rpe) && (
				<div>
					<span className="text-muted-foreground">RPE: </span>
					{result.rpe}
				</div>
			)}
			{hasContent(result?.notes) && (
				<div className="pt-1 italic text-muted-foreground">
					"{result.notes}"
				</div>
			)}
		</div>
	);
}

/**
 * Accordion del Diario: prescrizione (riusa ExerciseDetail della Dashboard)
 * + "Tuo risultato" che gestisce conditioning / superset / set-by-set / simple.
 *
 * @param {Object} exercise - Exercise object with result attached
 * @param {string} language - Language code (default: "it")
 * @param {boolean} isCoachView - If true, adjust labels and layout for coach viewing athlete result
 */
export function ExerciseDetailAccordion({
	exercise,
	language = "it",
	isCoachView = false,
}) {
	const result = exercise?.result;
	const isShootingExercise =
		exercise?.exercise_type === "dry_fire" ||
		exercise?.exercise_type === "live_fire";
	const isConditioningExercise =
		exercise?.exercise_type === "conditioning" ||
		exercise?.exercise_type === "running" ||
		hasContent(exercise?.conditioning_type) ||
		hasContent(result?.conditioning_type);

	const prescriptionBlock = (
		<>
			{/* Dettaglio prescrizione (riuso integrale da Dashboard) */}
			<div
				className={
					isCoachView
						? "rounded-sm border border-border/40 bg-background/20 px-3 py-3 space-y-2.5"
						: undefined
				}
			>
				{isCoachView && (
					<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
						Prescrizione
					</div>
				)}
				<ExerciseDetail
					exercise={exercise}
					exerciseLibrary={{}}
					loadSuggestions={{}}
					hideScorePreview={isCoachView}
					compactStage={isCoachView}
					onOpenMedia={undefined}
				/>
			</div>
		</>
	);
	const resultBlock = (
		<>
			{/* Risultato — specifico Diario o Coach View */}
			{result && (
				<div
					className={
						isCoachView
							? "rounded-sm border border-volt/15 bg-volt/[0.04] px-3 py-3 space-y-2.5"
							: "pt-3 border-t border-border/40"
					}
				>
					<div
						className={`text-[10px] uppercase tracking-wider font-medium ${
							isCoachView ? "text-volt" : "text-volt/80"
						}`}
					>
						{isCoachView ? "Risultato atleta" : "Tuo risultato"}
					</div>
					{exercise?.exercise_type === "running" ? (
						<RunningResultBlock exercise={exercise} result={result} />
					) : isShootingExercise ? (
						<ShootingResultBlock
							exercise={exercise}
							result={result}
							isCoachView={isCoachView}
						/>
					) : isConditioningExercise ? (
						<ConditioningResultBlock
							exercise={exercise}
							result={result}
							language={language}
							isCoachView={isCoachView}
						/>
					) : (
						<StrengthResultBlock
							result={result}
							prescription={exercise}
							isCoachView={isCoachView}
						/>
					)}
				</div>
			)}
		</>
	);
	// Tiro, lato coach: si apre per **valutare un risultato**, quindi il
	// risultato va in cima e la prescrizione sotto, compatta. Prima la
	// piantina occupava lo schermo e il risultato stava sotto la piega.
	const resultFirst = isCoachView && isShootingExercise && !!result;

	return (
		<div
			className={`text-sm ${
				isCoachView ? "px-4 pb-4 pt-3 space-y-4" : "px-3 pb-3 pt-2 space-y-3"
			}`}
		>
			{resultFirst ? resultBlock : prescriptionBlock}
			{resultFirst ? prescriptionBlock : resultBlock}
		</div>
	);
}
